import { z } from 'zod';

import {
  type ArtifactKind,
  type StaleReason,
  type StaleUpstreamKind,
  DRAFT_ARTIFACT_CHAIN,
  downstreamArtifactKinds,
  stalenessWindowsOf,
} from './artifacts.ts';
import { consequencesText } from './consequences-copy.ts';
import { DomainError } from './errors.ts';

/**
 * What a researcher can change that something below it depends on: the Study Type, the Materials,
 * the Goal, then each document of the chain. The audience is here too, so every change route asks
 * the same question, and answers that nothing sits below it.
 */
export const CHANGING_KINDS = ['study_type', 'materials', 'goal', ...DRAFT_ARTIFACT_CHAIN] as const;
export const changingKindSchema = z.enum(CHANGING_KINDS);
export type ChangingKind = z.infer<typeof changingKindSchema>;

/** Everything a change can reach below it, in the order the study's steps run. */
export const AFFECTED_KINDS = ['materials', 'goal', ...DRAFT_ARTIFACT_CHAIN] as const;
export type AffectedKind = (typeof AFFECTED_KINDS)[number];

/**
 * What a change does to one step below it:
 * - `recapture`: the Materials are saved again for the new Study Type before anything below them
 *   can be drafted;
 * - `options_refresh`: the Goal's options are drafted again from the changed inputs, and the chosen
 *   Goal stays;
 * - `redraft`: the research questions are drafted again from scratch for the new Goal;
 * - `reconcile`: the document is updated for the change on its card, keeping what the change does
 *   not bear on.
 */
export const CHANGE_EFFECTS = ['recapture', 'options_refresh', 'redraft', 'reconcile'] as const;
export type ChangeEffect = (typeof CHANGE_EFFECTS)[number];

/** An image a Materials change would take off the guide, and the question it is shown before. */
export const removedImageSchema = z.strictObject({
  questionId: z.string(),
  fileId: z.string(),
  /** The name the researcher gave the file, as the Materials list it. */
  name: z.string(),
});
export type RemovedImage = z.infer<typeof removedImageSchema>;

/**
 * What a change would do below it, worked out before it commits; see `changeConsequences`.
 *
 * Carried on a `change_unacknowledged` refusal as `detail.consequences`, so the screen and the agent
 * describe the change the server judged rather than one they worked out themselves.
 */
export const changeConsequencesSchema = z.strictObject({
  changing: changingKindSchema,
  /** Every step below the change that holds something, in step order. */
  affected: z.array(
    z.strictObject({
      kind: z.enum(AFFECTED_KINDS),
      effect: z.enum(CHANGE_EFFECTS),
      /**
       * The step is already waiting on a change of this kind, so this change only widens what it is
       * updated for. A repeated edit is not asked about again.
       */
      alreadyWaiting: z.boolean(),
    }),
  ),
  imagesRemoved: z.array(removedImageSchema),
  /** The study is ready to buy now, and the change takes it back to draft. */
  leavesReady: z.boolean(),
  /** The change newly affects something below it, so it commits only once acknowledged. */
  needsAcknowledgement: z.boolean(),
});
export type ChangeConsequences = z.infer<typeof changeConsequencesSchema>;

/** One artifact the study holds, as far as its consequences need it. */
export interface ConsequenceArtifact {
  kind: ArtifactKind;
  staleReason: StaleReason | null;
}

/** A step the change newly reaches in a way that has to be acknowledged. */
const asksAbout = (step: ChangeConsequences['affected'][number]): boolean =>
  !step.alreadyWaiting && step.effect !== 'options_refresh';

const waitsOn = (artifact: ConsequenceArtifact, upstream: StaleUpstreamKind): boolean =>
  stalenessWindowsOf(artifact.staleReason).some((window) => window.upstream_kind === upstream);

/**
 * What a change would do below it, from the study as it stands and with no model call: which steps
 * it reaches and how, which images it takes off the guide, and whether it has to be acknowledged
 * before it commits. The server decides under the study row lock with it, and the screen reads the
 * same answer before anything is changed.
 *
 * A change needs acknowledging when it newly affects something: a step below it not already waiting
 * on a change of this kind, Materials confirmed for the Study Type that a new type makes the
 * researcher save again, or an image a Materials change takes off the guide. A change whose every
 * step below is already waiting on its kind asks nothing, so repeated edits are not asked about
 * again. A Goal whose options are drafted again is listed and asks nothing on its own: the Goal
 * stays chosen.
 *
 * A Study Type change reaches the chain only through the Materials: once they are saved again for
 * the new type, that version updates the chain. While the Materials are not confirmed for the type
 * the study has, that save is already outstanding, so the chain is already waiting on it.
 */
export const changeConsequences = ({
  changing,
  artifacts,
  materialsConfirmed,
  readyNow,
  imagesRemoved = [],
}: {
  changing: ChangingKind;
  /** Every artifact the study holds now, with what each is waiting on. */
  artifacts: readonly ConsequenceArtifact[];
  /** The Materials are confirmed at the version the study holds, for the Study Type it has now. */
  materialsConfirmed: boolean;
  /** The study is `ready` now. */
  readyNow: boolean;
  /** The images a Materials change would take off the guide; empty for every other change. */
  imagesRemoved?: readonly RemovedImage[];
}): ChangeConsequences => {
  const held = (kind: AffectedKind) => artifacts.find((artifact) => artifact.kind === kind);
  const affected: ChangeConsequences['affected'] = [];
  const note = (kind: AffectedKind, effect: ChangeEffect, alreadyWaiting: boolean) => {
    if (held(kind) !== undefined) {
      affected.push({ kind, effect, alreadyWaiting });
    }
  };

  if (changing === 'study_type' || changing === 'materials') {
    if (changing === 'study_type') {
      note('materials', 'recapture', !materialsConfirmed);
    }
    note('goal', 'options_refresh', false);
    for (const kind of DRAFT_ARTIFACT_CHAIN) {
      const artifact = held(kind);
      note(
        kind,
        'reconcile',
        (changing === 'study_type' && !materialsConfirmed) ||
          (artifact !== undefined && waitsOn(artifact, 'materials')),
      );
    }
  } else {
    for (const kind of downstreamArtifactKinds(changing)) {
      const artifact = held(kind);
      note(
        kind,
        changing === 'goal' && kind === 'rqs' ? 'redraft' : 'reconcile',
        artifact !== undefined && waitsOn(artifact, changing),
      );
    }
  }

  return {
    changing,
    affected,
    imagesRemoved: [...imagesRemoved],
    leavesReady: readyNow,
    needsAcknowledgement: imagesRemoved.length > 0 || affected.some(asksAbout),
  };
};

/**
 * What a researcher acknowledged when they agreed to a change: the steps it would newly reach and
 * the images it would take off the guide, as the confirm they answered listed them. The server
 * judges the change again under the study row lock and makes it only when everything it would now
 * do is in here, so a step drafted, or an image placed, while the confirm was open is asked about
 * again rather than changed unseen.
 */
export const changeAcknowledgementSchema = z.strictObject({
  kinds: z.array(z.enum(AFFECTED_KINDS)),
  images: z.array(z.string()),
});
export type ChangeAcknowledgement = z.infer<typeof changeAcknowledgementSchema>;

/** What agreeing to these consequences acknowledges; see `changeAcknowledgementSchema`. */
export const acknowledgementOf = (consequences: ChangeConsequences): ChangeAcknowledgement => ({
  kinds: consequences.affected.filter(asksAbout).map((step) => step.kind),
  images: consequences.imagesRemoved.map((image) => image.fileId),
});

/**
 * Whether a change may commit: it needs no acknowledgement, or everything it would now ask about —
 * each step it newly reaches and each image it takes off the guide — was acknowledged.
 */
export const changeAcknowledged = (
  consequences: ChangeConsequences,
  acknowledged: ChangeAcknowledgement | null,
): boolean => {
  if (!consequences.needsAcknowledgement) {
    return true;
  }
  if (acknowledged === null) {
    return false;
  }
  const needed = acknowledgementOf(consequences);

  return (
    needed.kinds.every((kind) => acknowledged.kinds.includes(kind)) &&
    needed.images.every((fileId) => acknowledged.images.includes(fileId))
  );
};

/**
 * The refusal of a change that has to be acknowledged first, as data: the sentence a researcher is
 * told, and the consequences themselves as `detail.consequences`, so the screen lists what the
 * server judged and the agent can say it in plain words before it asks.
 */
export const changeUnacknowledged = (consequences: ChangeConsequences): DomainError =>
  new DomainError('change_unacknowledged', consequencesText(consequences), { consequences });
